// src/common/utils/citation-utils.js

/**
 * Normalize a string for comparison: trim, collapse whitespace, normalize quotes/dashes, lowercase.
 */
export function normalizeString(s) {
  return (s || '').toString().trim()
    .replace(/\s+/g, ' ')
    .replace(/["""']/g, '"')
    .replace(/[–—]/g, '-')
    .toLowerCase();
}

/**
 * Get the first element of an array, or null if empty/not an array.
 */
export function first(arr) {
  return Array.isArray(arr) && arr.length ? arr[0] : null;
}

/**
 * A cross-reference whose target had not been attempted yet when it ran. This is a
 * transient ordering outcome, not a verdict: it must be retried, never reused.
 */
export function isUnresolvedCrossRefTarget(resolution) {
  return (resolution?.resolve_result || resolution)?.method === 'cross-ref-unresolved-target';
}

/**
 * Build a resolution key for a citation's *work* (not its pinpoint).
 * Priority: DOI > URL > explicit id_number > normalized (type,title,container,year,authors).
 * Pinpoints (pages/paras/§) are intentionally ignored; they're checked in congruence.
 */
export function resolutionKeyFromCitation(cite) {
  const s = cite?.source || {};

  // 0) Cross-references resolve to another citation, not to their own (often
  //    pinpoint-bearing) raw text. Key them by their TARGET so that e.g.
  //    "Smith, supra note 5, at 22" and "…at 40" resolve once.
  if (cite?.is_cross_reference && cite?.cross_reference) {
    const cr = cite.cross_reference;
    const strategy = normalizeString(cr.strategy);
    if (strategy === 'by_number' && cr.target_number != null) {
      return `xref:num:${normalizeString(cr.target_number)}`;
    }
    if (strategy === 'by_short_name' && cr.short_name) {
      return `xref:name:${normalizeString(cr.short_name)}`;
    }
    // PREVIOUS (ibid/id) is position-dependent and cannot be shared across
    // anchors — key it uniquely so each is resolved on its own.
    return `xref:prev:${normalizeString(cite.id || s.raw)}`;
  }

  // 1) Strong identifiers
  if (s.authority_tag) return `tag:${normalizeString(s.authority_tag)}`;
  if (s.doi) return `doi:${normalizeString(s.doi)}`;
  if (s.url) return `url:${normalizeString(s.url)}`;
  if (s.id_number) return `id:${normalizeString(s.id_number)}`;

  // 2) Structured biblio
  // Handle type properly - it can be a string or an object
  let typeStr = '';
  if (typeof s.type === 'string') {
    typeStr = s.type;
  } else if (s.type && typeof s.type === 'object') {
    // Handle Precedent object with origin
    if (s.type.origin) {
      typeStr = `precedent-${s.type.origin}`;
    }
    // Handle LegalNorm object with jurisdiction
    else if (s.type.jurisdiction) {
      typeStr = `legalnorm-${s.type.jurisdiction}`;
    }
    // Handle objects with subtype (Academic, InCase, News, Other)
    else if (s.type.subtype) {
      typeStr = s.type.subtype;
    }
    // Fallback for unknown object types
    else {
      typeStr = JSON.stringify(s.type);
    }
  }
  const type = normalizeString(typeStr);

  // Provision/identifier (see note at 3b). Computed before `title` because whether we
  // may drop the noisy `raw` fallback depends on it.
  const explicitCode = normalizeString(stripTrailingPinpoint(s.code));
  // A legal-norm `code` is a provision ("article 11") that is NOT unique across
  // instruments, so the instrument must still come from `title`/`raw`. Any other
  // explicit `code` (exhibit/authority tag like "C-21", reporter cite) IS globally
  // unique, so `raw` is pure noise that would fragment the key across phrasings.
  const codeIsGloballyUnique = !!explicitCode && !type.startsWith('legalnorm');
  const titleFallback = codeIsGloballyUnique ? '' : s.raw;
  const title = normalizeString(s.title || s.case_name || s.report_title || titleFallback);
  const container = normalizeString(s.container || s.journal || s.reporter || s.series);
  const year = normalizeString(s.year);
  const vol = normalizeString(s.volume || s.vol);
  const no  = normalizeString(s.issue || s.no);
  const court = normalizeString(s.court || s.tribunal);

  // 3) Soft identity via authors (use first author only for stability)
  let author = '';
  const a1 = first(s.authors) || first(s.author) || s.author;
  if (a1) author = typeof a1 === 'string' ? normalizeString(a1) : normalizeString(a1.family || a1.last || a1.name);

  // 3b) Provision identifier is part of the WORK's identity, not a pinpoint:
  //     "article 514" and "article 700" of the same code are DIFFERENT norms.
  //     The biblio fields above (esp. title) frequently carry only the code/statute
  //     name (e.g. title="décret n°67-223 du 17 mars 1967"), which would otherwise
  //     collapse every article of that text into ONE resolution group that shares a
  //     single (wrong) resolved document. Prefer the explicit s.code; when it is
  //     absent the article number lives only in s.raw (which the decree-level title
  //     shadows), so extract it from there. A trailing reporter pinpoint is stripped
  //     ("123 F.3d 456, 460" -> "123 F.3d 456") so pinpoint variants of one case dedup.
  let provision = explicitCode;
  if (!provision) provision = provisionFromRaw(s.raw);

  // 4) Build a compact key
  const parts = [type, title, container, year, vol, no, court, author, provision].filter(Boolean);
  const base = parts.length ? parts.join('|') : normalizeString(s.raw);
  return `biblio:${base}`;
}

/**
 * Strip a trailing reporter pinpoint from a document identifier so pinpoint-only
 * variants of the same work still share a key.
 *   "123 F.3d 456, 460"    -> "123 F.3d 456"
 *   "550 U.S. 544, 570-72" -> "550 U.S. 544"
 * Leaves provision identifiers untouched ("article 700", "L. 121-1", "articles 514
 * et suivants"), since those have no trailing ", <page>".
 */
export function stripTrailingPinpoint(code) {
  return (code || '').toString().replace(/,\s*\d+(?:\s*[-–—]\s*\d+)?\s*$/, '').trim();
}

/**
 * Extract the article/provision designation from a legal-norm citation's raw text,
 * used as an identity discriminator when source.code is absent. Returns '' when no
 * provision marker is found (so non-legislative citations are unaffected — it only
 * ever SPLITS groups, never merges, so a spurious match just costs one extra resolve).
 *   "article 11 du décret n°67-223"      -> "article 11"
 *   "L'article 9 – III de la loi…"        -> "article 9 iii"
 *   "articles 514 et suivants du code…"   -> "article 514 et suivants"
 *   "article 10-1 de la loi…"             -> "article 10-1"
 *   "L. 121-1 du code de l'urbanisme"     -> "l 121-1"
 */
export function provisionFromRaw(raw) {
  const s = (raw || '').toString();
  // "article(s) N", "art. N", with optional codified prefix (L./R./D.), hyphenated
  // sub-number (10-1), roman/numeric sub-part (– III), or "et suivants".
  const m = s.match(
    /\b(?:articles?|art\.?)\s*([LRD]\.?\s*)?(\d+(?:[-‑–]\d+)*)((?:\s*[-‑–—]\s*[IVXLC]+\b)|(?:\s+et\s+suivants?\b))?/i
  );
  if (m) {
    const prefix = (m[1] || '').replace(/[\s.]+/g, '');
    const num = m[2];
    const suffix = m[3] || '';
    return normalizeString(`article ${prefix}${num} ${suffix}`);
  }
  // Bare codified reference without the word "article": "L. 121-1", "R 421-1".
  const m2 = s.match(/\b([LRD])\.?\s*(\d+(?:[-‑–]\d+)*)\b/);
  if (m2) return normalizeString(`${m2[1]} ${m2[2]}`);
  return '';
}

/**
 * Score how much resolvable identity a citation carries. Used to pick which
 * member of a dedup group is resolved: short forms ("Smith at 22") share a key
 * with the full citation, and resolving the richest member gives the search the
 * most signal. Strong identifiers outweigh biblio fields; raw length only
 * breaks ties. Ported to Python as citation_keys.citation_richness — keep in sync.
 */
export function citationRichness(cite) {
  const s = cite?.source || {};
  let score = 0;
  if (s.authority_tag) score += 4;
  if (s.doi) score += 4;
  if (s.url) score += 3;
  if (s.id_number) score += 3;
  const fields = ['title', 'case_name', 'report_title', 'container', 'journal', 'reporter',
                  'series', 'year', 'volume', 'vol', 'issue', 'no', 'court', 'tribunal', 'code'];
  for (const f of fields) {
    const v = s[f];
    if (v != null && String(v).trim()) score += 1;
  }
  const authors = s.authors || s.author;
  if (Array.isArray(authors) ? authors.length : authors) score += 1;
  return score * 1000 + Math.min(String(s.raw || '').length, 400);
}

/**
 * Pick the entry whose citation is richest (see citationRichness). Entries may
 * wrap the citation; pass getCitation to unwrap. First among ties wins, so the
 * choice is stable for equally-detailed members.
 */
export function pickRichestCitation(entries, getCitation = (e) => e) {
  let best = null;
  let bestScore = -1;
  for (const entry of entries || []) {
    const score = citationRichness(getCitation(entry));
    if (score > bestScore) {
      best = entry;
      bestScore = score;
    }
  }
  return best;
}
